import { useState } from 'react'
import BuyCreditsModal from '../components/buyCreditsModal'
import DifficultyBadge from '../components/difficultyBadge'
import { GRADE_CONFIG } from '../constants/config'
import { STRINGS } from '../constants/strings'
import { useAuth, useExam } from '../hooks'

const GRADE_CFG = Object.fromEntries(
  Object.entries(GRADE_CONFIG).map(([k, v]) => [k, { ...v, border: '' }])
)

function ScoreRing({ pct, color }) {
  const r   = 52
  const cir = 2 * Math.PI * r
  return (
    <div className="relative w-36 h-36 mx-auto">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="var(--surface-2)" strokeWidth="8" />
        <circle
          cx="60" cy="60" r={r} fill="none" strokeWidth="8"
          stroke={color} strokeLinecap="round"
          strokeDasharray={cir}
          strokeDashoffset={cir * (1 - pct / 100)}
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-black text-3xl text-[var(--text-1)]">{Math.round(pct)}%</span>
      </div>
    </div>
  )
}

function AnswerRow({ r, idx }) {
  const q        = r.question
  const msqPartial = q.type === 'msq' && r.creditEarned > 0 && r.creditEarned < 1
  // is_correct === null means "never graded" (a credits block mid-attempt) —
  // distinct from is_correct === false ("graded, and it was wrong"). Without
  // this, an ungraded question fell through to the same red "Wrong" label as
  // a genuinely incorrect answer, which is misleading.
  const ungraded = r.is_correct === null
  const correct  = !ungraded && (r.is_correct === true || r.verdict === 'Correct')
  const partial  = !ungraded && (r.verdict === 'Partially Correct' || msqPartial)
  const bgColor  = ungraded ? 'rgba(148,163,184,0.06)' : correct ? 'rgba(74,222,128,0.06)' : partial ? 'rgba(251,191,36,0.06)' : 'rgba(248,113,113,0.06)'
  const bdColor  = ungraded ? 'rgba(148,163,184,0.2)'  : correct ? 'rgba(74,222,128,0.2)'  : partial ? 'rgba(251,191,36,0.2)'  : 'rgba(248,113,113,0.2)'
  const dotColor = ungraded ? '#94a3b8' : correct ? '#4ade80' : partial ? '#fbbf24' : '#f87171'
  const label    = ungraded ? 'Ungraded' : correct ? 'Correct' : partial ? 'Partial' : 'Wrong'

  return (
    <div className="p-4 sm:p-5" style={{ borderBottom: '1px solid var(--border)' }}>
      <div className="flex items-start gap-3">
        {/* Index + status dot */}
        <div className="flex flex-col items-center gap-1.5 flex-shrink-0 pt-0.5">
          <span className="text-xs font-mono font-bold w-6 text-center" style={{ color: 'var(--text-3)' }}>{idx + 1}</span>
          <div className="w-2 h-2 rounded-full" style={{ background: dotColor }} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3 mb-3">
            <p className="text-sm font-medium leading-snug" style={{ color: 'var(--text-1)' }}>{q.question}</p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-md" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }}>
                {r.marks ?? 1} {r.marks === 1 ? 'mark' : 'marks'}
              </span>
              <span className="text-[0.65rem] font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: bgColor, border: `1px solid ${bdColor}`, color: dotColor }}>
                {label}
              </span>
            </div>
          </div>

          {q.type === 'mcq' && (
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span style={{ color: 'var(--text-3)' }}>Your answer:</span>
                <span className="font-medium" style={{ color: correct ? '#4ade80' : '#f87171' }}>{r.user_answer || 'Not answered'}</span>
              </div>
              {!correct && (
                <div className="flex items-center gap-2">
                  <span style={{ color: 'var(--text-3)' }}>Correct:</span>
                  <span className="font-medium text-emerald-400">{q.correct_answer}) {q.options[q.correct_answer]}</span>
                </div>
              )}
              {q.explanation && (
                <p className="mt-2 text-xs leading-relaxed px-3 py-2 rounded-lg" style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>
                  {q.explanation}
                </p>
              )}
            </div>
          )}

          {q.type === 'msq' && (
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span style={{ color: 'var(--text-3)' }}>Your answer:</span>
                <span className="font-medium" style={{ color: correct ? '#4ade80' : partial ? '#fbbf24' : '#f87171' }}>
                  {Array.isArray(r.user_answer) && r.user_answer.length > 0 ? r.user_answer.join(', ') : 'Not answered'}
                </span>
              </div>
              {!correct && (
                <div className="flex items-center gap-2">
                  <span style={{ color: 'var(--text-3)' }}>Correct:</span>
                  <span className="font-medium text-emerald-400">{(q.correct_answers ?? []).join(', ')}</span>
                </div>
              )}
              {q.explanation && (
                <p className="mt-2 text-xs leading-relaxed px-3 py-2 rounded-lg" style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>
                  {q.explanation}
                </p>
              )}
            </div>
          )}

          {q.type === 'true_false' && (
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span style={{ color: 'var(--text-3)' }}>Your answer:</span>
                <span className="font-medium" style={{ color: correct ? '#4ade80' : '#f87171' }}>{r.user_answer || 'Not answered'}</span>
              </div>
              {!correct && (
                <div className="flex items-center gap-2">
                  <span style={{ color: 'var(--text-3)' }}>Correct:</span>
                  <span className="font-medium text-emerald-400">{q.correct_answer}</span>
                </div>
              )}
            </div>
          )}

          {q.type === 'short_answer' && (
            <div className="text-xs space-y-2">
              <div className="flex items-start gap-2">
                <span style={{ color: 'var(--text-3)', flexShrink: 0 }}>Your answer:</span>
                <span className="font-medium" style={{ color: r.user_answer ? 'var(--text-2)' : '#f87171' }}>{r.user_answer || 'Not answered'}</span>
              </div>
              {r.feedback && <p className="font-medium" style={{ color: dotColor }}>{r.feedback}</p>}
              {(r.keyPointsCovered?.length > 0 || r.keyPointsMissed?.length > 0) && (
                <div className="space-y-1">
                  {r.keyPointsCovered?.map((kp, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <span className="text-emerald-400 font-bold flex-shrink-0">✓</span>
                      <span style={{ color: 'var(--text-2)' }}>{kp}</span>
                    </div>
                  ))}
                  {r.keyPointsMissed?.map((kp, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <span className="text-red-400 font-bold flex-shrink-0">✗</span>
                      <span style={{ color: 'var(--text-3)' }}>{kp}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="px-3 py-2 rounded-lg leading-relaxed" style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>
                <span style={{ color: 'var(--text-3)' }}>Model answer: </span>{q.correct_answer}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function downloadScorecard(score, difficulty, mode, cfg, correct, wrong) {
  const W = 640, H = 360, S = 2
  const canvas = document.createElement('canvas')
  canvas.width = W * S; canvas.height = H * S
  const c = canvas.getContext('2d')
  c.scale(S, S)

  // background
  c.fillStyle = '#0f172a'
  c.fillRect(0, 0, W, H)

  // top accent bar
  c.fillStyle = cfg.color
  c.fillRect(0, 0, W, 5)

  // brand label
  c.fillStyle = '#64748b'
  c.font = '600 11px system-ui'
  c.letterSpacing = '2px'
  c.fillText('SKILLASSESS  ·  SCORECARD', 36, 38)
  c.letterSpacing = '0px'

  // grade emoji + text
  c.font = 'bold 52px system-ui'
  c.fillStyle = cfg.color
  c.fillText(score.grade, 36, 118)

  // percentage
  c.font = 'bold 28px system-ui'
  c.fillStyle = '#f8fafc'
  c.fillText(`${score.percentage.toFixed(0)}%`, 36, 158)

  // pass/fail pill (drawn as text)
  const pillText = score.passed ? '✓  PASSED' : '✗  FAILED'
  c.font = 'bold 13px system-ui'
  c.fillStyle = score.passed ? '#4ade80' : '#f87171'
  c.fillText(pillText, 36, 188)

  // mode + difficulty
  c.font = '13px system-ui'
  c.fillStyle = '#94a3b8'
  c.fillText(`${mode === 'virtual' ? STRINGS.modes.virtual : STRINGS.modes.practice}  ·  ${difficulty}`, 36, 215)

  // divider
  c.strokeStyle = '#1e293b'
  c.lineWidth = 1
  c.beginPath(); c.moveTo(36, 236); c.lineTo(W - 36, 236); c.stroke()

  // stats row
  const stats = [
    { label: 'Correct', value: correct, color: '#4ade80' },
    { label: 'Wrong',   value: wrong,   color: '#f87171' },
    { label: 'Total',   value: correct + wrong, color: '#f8fafc' },
    { label: 'Points',  value: `${score.score.toFixed(1)} / ${score.max_score}`, color: '#f8fafc' },
  ]
  stats.forEach(({ label, value, color }, i) => {
    const x = 36 + i * 148
    c.font = 'bold 22px system-ui'
    c.fillStyle = color
    c.fillText(value, x, 280)
    c.font = '11px system-ui'
    c.fillStyle = '#64748b'
    c.fillText(label, x, 300)
  })

  // footer
  c.font = '11px system-ui'
  c.fillStyle = '#334155'
  c.fillText(`skillassess.app  ·  ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`, 36, H - 20)

  const link = document.createElement('a')
  link.download = `scorecard-${score.grade}-${score.percentage.toFixed(0)}pct.png`
  link.href = canvas.toDataURL('image/png')
  link.click()
}

function bold(text) {
  return text.replace(/\*\*(.*?)\*\*/g, '$1')
}

function PrepSection({ tips }) {
  return (
    <div className="space-y-1.5">
      {tips.split('\n').map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-2" />
        if (line.startsWith('## '))
          return (
            <h3 key={i} className="text-xs font-bold uppercase tracking-widest pt-4 pb-1 first:pt-0"
                style={{ color: 'var(--brand)' }}>
              {line.slice(3)}
            </h3>
          )
        if (line.startsWith('- '))
          return (
            <div key={i} className="flex gap-3 py-1">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--brand)' }} />
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{bold(line.slice(2))}</p>
            </div>
          )
        if (/^\d+\./.test(line)) {
          const [num, ...rest] = line.split('. ')
          return (
            <div key={i} className="flex gap-3 py-1">
              <span className="text-xs font-bold flex-shrink-0 w-5 text-right mt-0.5" style={{ color: 'var(--brand)' }}>{num}.</span>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{bold(rest.join('. '))}</p>
            </div>
          )
        }
        return <p key={i} className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>{bold(line)}</p>
      })}
    </div>
  )
}

export default function ResultsScreen() {
  const { state, reset } = useExam()
  const { user, updateUser } = useAuth()
  const { score, results, breakdown, prepTips, difficulty, mode, autoSubmitted, submitReason, creditsBlockedForGrading } = state
  const [view, setView] = useState('summary') // 'summary' | 'report' | 'review'
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false)
  // Same "has its own billable balance" test as profilePage's hasOwnBalance —
  // an org admin previewing their own exam bills to their own org's balance
  // and can buy credits directly, same as a standalone user. Only an
  // org-linked examinee (role 'user' WITH an orgId) has no balance of their
  // own and must be told to contact their organisation instead.
  const canBuyCreditsDirectly = user?.role === 'organisation' || (user?.role === 'user' && !user?.orgId)

  if (!score || !results.length) {
    return (
      <div className="text-center py-24">
        <p className="text-[var(--text-3)] text-sm mb-4">No results found.</p>
        <button onClick={reset} className="btn-ghost">← Back to Setup</button>
      </div>
    )
  }

  const cfg       = GRADE_CFG[score.grade] ?? { color: '#94a3b8', emoji: '📊', bg: 'bg-slate-500/10', border: 'border-slate-500/20' }
  const passColor = score.passed ? '#4ade80' : '#f87171'
  const passBg    = score.passed ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border-red-500/30 text-red-400'

  const isMsqPartial = r => r.question.type === 'msq' && r.creditEarned > 0 && r.creditEarned < 1
  const correct  = results.filter(r => r.is_correct === true || r.verdict === 'Correct').length
  const partial  = results.filter(r => r.verdict === 'Partially Correct' || isMsqPartial(r)).length
  const wrong    = results.filter(r => r.is_correct === false && r.verdict !== 'Partially Correct' && !isMsqPartial(r)).length

  // ── Study Report page ────────────────────────────────────────
  if (view === 'report') {
    return (
      <div
        className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-10 animate-fade-up"
        style={{ userSelect: 'none' }}
        onCopy={e => e.preventDefault()}
        onContextMenu={e => e.preventDefault()}
      >

        {/* Back */}
        <button onClick={() => setView('summary')} className="btn-ghost mb-8 text-sm">← Back to Results</button>

        {/* Page header */}
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--brand)' }}>AI-Generated</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-2" style={{ color: 'var(--text-1)' }}>
            Personalised Study Report
          </h2>
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>
            {results.length} questions · Score {score.percentage.toFixed(0)}% ·{' '}
            <span style={{ color: score.passed ? '#4ade80' : '#f87171' }}>{score.passed ? 'Passed ✓' : 'Failed ✗'}</span>
          </p>
        </div>

        {/* Score bar */}
        <div className="card p-5 mb-8 flex items-center gap-5">
          <div className="flex-1">
            <div className="flex justify-between text-sm mb-2">
              <span style={{ color: 'var(--text-2)' }}>Overall score</span>
              <span className="font-bold" style={{ color: cfg.color }}>{score.percentage.toFixed(0)}%</span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${score.percentage}%`, background: cfg.color }} />
            </div>
          </div>
          <span className={`px-4 py-1.5 rounded-full text-sm font-bold border ${passBg}`}>
            {score.passed ? 'PASSED' : 'FAILED'}
          </span>
        </div>

        {/* Report content */}
        <div className="card p-6 sm:p-8">
          {prepTips ? <PrepSection tips={prepTips} /> : (
            <div className="text-center py-10">
              <p className="text-4xl mb-4">{score.passed ? '🎉' : '📚'}</p>
              <p className="text-lg font-bold mb-2" style={{ color: 'var(--text-1)' }}>
                {score.passed ? 'Great performance!' : 'Keep practising!'}
              </p>
              <p className="text-sm max-w-sm mx-auto" style={{ color: 'var(--text-3)' }}>
                {score.passed ? 'You passed. Review any weak topics to solidify your knowledge.' : 'Focus on topics where you scored below 50%.'}
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── Review Answers page ──────────────────────────────────────
  if (view === 'review') {
    return (
      <div
        className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-10 animate-fade-up"
        style={{ userSelect: 'none' }}
        onCopy={e => e.preventDefault()}
        onContextMenu={e => e.preventDefault()}
      >

        {/* Back */}
        <button onClick={() => setView('summary')} className="btn-ghost mb-8 text-sm">← Back to Results</button>

        {/* Page header */}
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--brand)' }}>Answer Review</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-2" style={{ color: 'var(--text-1)' }}>
            Review Answers
          </h2>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { v: results.length, l: 'Total',   c: 'var(--text-1)' },
            { v: correct,        l: 'Correct', c: '#4ade80' },
            { v: partial,        l: 'Partial', c: '#fbbf24' },
            { v: wrong,          l: 'Wrong',   c: '#f87171' },
          ].map(({ v, l, c }) => (
            <div key={l} className="card p-5 text-center">
              <div className="text-3xl font-black mb-1" style={{ color: c }}>{v}</div>
              <div className="text-xs font-medium" style={{ color: 'var(--text-3)' }}>{l}</div>
            </div>
          ))}
        </div>

        {/* Answer list */}
        <div className="card overflow-hidden">
          {results.map((r, i) => <AnswerRow key={i} r={r} idx={i} />)}
        </div>
      </div>
    )
  }

  // ── Summary page (default) ───────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-10 animate-fade-up">
      {creditsBlockedForGrading && (
        <div className="mb-6 flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          <span className="flex-1 min-w-[200px]">
            ⚠ Some short-answer questions couldn't be AI-graded — {canBuyCreditsDirectly ? 'your' : "your organisation's"} credit balance ran out mid-Trial, so they're excluded from your score.
            {!canBuyCreditsDirectly && ' Contact your organisation to top up.'}
          </span>
          {canBuyCreditsDirectly && (
            <button onClick={() => setBuyCreditsOpen(true)} className="btn-primary text-xs flex-shrink-0">Buy Credits</button>
          )}
        </div>
      )}
      {buyCreditsOpen && (
        <BuyCreditsModal user={user} updateUser={updateUser} showToast={() => {}} onClose={() => setBuyCreditsOpen(false)} />
      )}
      {autoSubmitted && (
        <div className="mb-6 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm">
          {submitReason === 'tabSwitch'
            ? STRINGS.results.autoSubmittedTab
            : submitReason === 'sessionExpired'
            ? STRINGS.results.autoSubmittedSession
            : submitReason === 'screenShare'
            ? STRINGS.results.autoSubmittedScreenShare
            : STRINGS.results.autoSubmitted}
        </div>
      )}

      <div className="grid lg:grid-cols-5 lg:items-start gap-6">

        {/* ── LEFT col: score + stats ── */}
        <div className="lg:col-span-3 flex flex-col gap-4">
          <div className="card p-8 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-[var(--text-3)] mb-6">
              <span>{mode === 'virtual' ? `⏱ ${STRINGS.modes.virtual}` : `📖 ${STRINGS.modes.practice}`}</span>
              {/* <span>·</span>
              <DifficultyBadge level={difficulty} /> */}
            </div>
            <ScoreRing pct={score.percentage} color={cfg.color} />
            <div className="mt-5">
              <span className="text-3xl mr-2">{cfg.emoji}</span>
              <span className="font-black text-2xl" style={{ color: cfg.color }}>{score.grade}</span>
            </div>
            <p className="text-base mt-2" style={{ color: 'var(--text-3)' }}>
              {score.score.toFixed(1)} / {score.max_score} pts
            </p>
            <div className="flex items-center justify-center gap-3 mt-5">
              <span className={`px-5 py-2 rounded-full border text-base font-bold ${passBg}`}>
                {score.passed ? 'PASSED ✓' : 'FAILED ✗'}
              </span>
              <span className="text-sm" style={{ color: 'var(--text-3)' }}>cutoff {score.cutoff_percentage}%</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[
              { v: `${score.percentage.toFixed(0)}%`,       l: 'Score',  c: cfg.color },
              { v: score.score.toFixed(1),                   l: 'Points', c: 'var(--text-1)' },
              { v: score.passed ? '✓ Passed' : '✗ Failed', l: 'Result', c: passColor },
            ].map(({ v, l, c }) => (
              <div key={l} className="card p-4 text-center">
                <div className="font-black text-xl" style={{ color: c }}>{v}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>{l}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button onClick={reset} className="btn-primary w-full justify-center">← Back to Home</button>
            <button onClick={() => setView('review')}
              className="w-full flex items-center justify-center gap-2 rounded-xl border text-sm font-semibold transition-all px-3 py-2"
              style={{ background: 'var(--card-bg)', borderColor: 'var(--border)', color: 'var(--text-1)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.background = 'var(--surface-2)' }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--card-bg)' }}
            >
              📋 Review {results.length} Answers
            </button>
          </div>

          {/* Share / Download — commented out
          {(() => {
            const shareText = `🎓 SkillAssess Scorecard\nScore: ${score.percentage.toFixed(0)}% | Grade: ${score.grade} | ${score.passed ? 'Passed ✓' : 'Failed ✗'}\nCorrect: ${correct} | Wrong: ${wrong} | Total: ${results.length}\nMode: ${mode === 'virtual' ? STRINGS.modes.virtual : STRINGS.modes.practice} · ${difficulty}`
            const waUrl    = `https://wa.me/?text=${encodeURIComponent(shareText)}`
            const mailUrl  = `mailto:?subject=${encodeURIComponent('My SkillAssess Scorecard')}&body=${encodeURIComponent(shareText)}`
            const btnBase  = 'w-8 h-8 rounded-lg flex items-center justify-center transition-all flex-shrink-0'
            const btnStyle = { background: 'var(--surface-2)', border: '1px solid var(--border)' }
            return (
              <div className="flex items-center gap-2 pt-1">
                <button onClick={() => downloadScorecard(score, difficulty, mode, cfg, correct, wrong)}
                  className={btnBase} style={{ ...btnStyle, color: 'var(--brand)' }}
                  title="Download scorecard">...</button>
                <a href={waUrl} target="_blank" rel="noreferrer" ...>...</a>
                <a href={mailUrl} ...>...</a>
              </div>
            )
          })()} */}
        </div>

        {/* ── RIGHT col: breakdown ── */}
        <div className="lg:col-span-2 flex flex-col lg:sticky lg:top-20" style={{ maxHeight: 'calc(100vh - 6rem)' }}>
          {Object.keys(breakdown).length > 0 && (
            <div className="card p-5 flex flex-col overflow-hidden" style={{ height: '100%' }}>
              <h3 className="text-xs font-semibold uppercase tracking-widest mb-4 flex-shrink-0" style={{ color: 'var(--text-3)' }}>Topic Performance</h3>
              <div className="space-y-3 overflow-y-auto flex-1">
                {Object.entries(breakdown)
                  .sort(([, a], [, b]) => a.correct / Math.max(a.total, 1) - b.correct / Math.max(b.total, 1))
                  .map(([topic, stats]) => {
                    const acc = stats.correct / Math.max(stats.total, 1)
                    const bar = acc >= 0.7 ? 'var(--green)' : acc >= 0.5 ? 'var(--amber)' : 'var(--red)'
                    return (
                      <div key={topic}>
                        <div className="flex justify-between text-xs mb-1.5">
                          <span style={{ color: 'var(--text-2)' }}>{topic}</span>
                          <span className="font-semibold" style={{ color: bar }}>{(acc * 100).toFixed(0)}%</span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${acc * 100}%`, background: bar }} />
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          )}

          {/* <button onClick={() => setView('report')} className="flex-shrink-0 w-full flex items-center justify-between px-5 py-4 rounded-2xl border text-sm font-semibold transition-all"
            style={{ background: 'var(--card-bg)', borderColor: 'var(--border)', color: 'var(--text-1)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.background = 'var(--surface-2)' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--card-bg)' }}
          >
            <span>🎯 Personalised Study Report</span>
            <span style={{ color: 'var(--text-3)' }}>→</span>
          </button> */}
        </div>
      </div>
    </div>
  )
}
